import { useEffect, useRef, useState } from "react";
import { Grid } from "@mui/material";
import { WavRecorder, WavStreamPlayer } from "../../lib/wavtools";
import { base64ToArrayBuffer } from "../../lib/utils";
import { useNavigate } from "react-router-dom";
import BackButton from "../../components/BackButton";

const API_URL = process.env.API_URL;

const Conversation = () => {
  const navigate = useNavigate();

  const [isRecording, setIsRecording] = useState(false);
  const ws = useRef<WebSocket>(null!);

  const wavRecorderRef = useRef<WavRecorder>(
    new WavRecorder({ sampleRate: 24000 }),
  );

  const wavStreamPlayerRef = useRef<WavStreamPlayer>(
    new WavStreamPlayer({ sampleRate: 24000 }),
  );

  useEffect(() => {
    let userData: {
      full_name: string;
      email: string;
      checkin_code: string;
      employee_name: string;
      isCheckin: boolean;
    } | null = null;
    ws.current = new WebSocket(API_URL + "/network/conversation");

    ws.current.onopen = () => {
      startRecord();
    };

    ws.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      const wavStreamPlayer = wavStreamPlayerRef.current;

      if (data?.type === "response.audio.delta") {
        const arrayBuffer = base64ToArrayBuffer(data.delta);
        const audioBuffer = new Int16Array(arrayBuffer);

        wavStreamPlayer.add16BitPCM(audioBuffer);
      }

      if (data?.type === "response.function_call_arguments.done") {
        const isCheckin = data.name === "checkin_visitor";
        const argumentsData = JSON.parse(data.arguments);

        if (!userData) {
          userData = {
            isCheckin,
            ...argumentsData,
          };
        }
      }

      if (data?.type === "response.done" && !!userData) {
        navigate(
          `/access/${userData.isCheckin ? "checkin" : "checkout"}/manual`,
          { state: userData },
        );
        userData = null;
      }
    };

    ws.current.onerror = async (error) => {
      const wavRecorder = wavRecorderRef.current;
      const wavStreamPlayer = wavStreamPlayerRef.current;

      await wavRecorder.end();
      await wavStreamPlayer.interrupt();

      setIsRecording(() => false);
    };

    return () => {
      const wavRecorder = wavRecorderRef.current;
      const wavStreamPlayer = wavStreamPlayerRef.current;

      ws.current.close();
      wavRecorder.end();
      wavStreamPlayer.interrupt();

      userData = null;
      setIsRecording(() => false);
    };
  }, []);

  const startRecord = async () => {
    const recorder = wavRecorderRef.current;
    const streamPlayer = wavStreamPlayerRef.current;

    await recorder.begin();
    await streamPlayer.connect();

    setIsRecording(() => true);

    await recorder.record((data) => {
      if (ws.current.readyState !== WebSocket.OPEN) {
        return;
      }

      ws.current.send(data.mono);
    });
  };

  useEffect(() => {
    if (ws.current.readyState === WebSocket.OPEN) {
      startRecord();

      return () => {
        if (!isRecording) {
          return;
        }

        const recorder = wavRecorderRef.current;
        const streamPlayer = wavStreamPlayerRef.current;

        setIsRecording(() => false);

        recorder.end();
        streamPlayer.interrupt();

        ws.current.close();
      };
    }
  }, []);

  return (
    <>
      <style>{`
        .animated-circle {
          aspect-ratio: 1.3;
          height: 25vh;
          background-color: #3498db;
          border-radius: 50%;
          animation: wobble 1s infinite;
          filter: blur(5px);
        }

        @keyframes wobble {
          25% {
            transform: scale(1.1) rotate(5deg);
            filter: blur(2px);
          }
          50% {
            transform: scale(1.2) rotate(-5deg);
          }
          75% {
            transform: scale(1.1) rotate(5deg);
          }
          100% {
            transform: scale(1) rotate(0deg);
            filter: blur(5px);
          }
        }
      `}</style>
      <div className="d-flex align-items-center flex-column h-100">
        <BackButton to={"/"} />
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <Grid item>
            <div className="animated-circle"></div>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default Conversation;
